import PropTypes from 'prop-types'
import { Button } from 'antd'
import { DatabaseOutlined, FileSearchOutlined } from '@ant-design/icons'
import { Link } from 'react-router-dom'
import { createUseStyles } from 'react-jss'
import dayjs from '../../lib/datetime'
import { toPath } from '../../routes'

// Both Orders tabs share this module so the Inbox and Missing cards have the
// exact same anatomy: name / MRN · date / identifiers / status or action line.

// Width of antd's expand-icon column. The expanded details are indented by the
// same amount so their fields line up with the card above them.
export const EXPAND_COLUMN_WIDTH = 40

const useStyles = createUseStyles({
  card: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  },
  name: {
    fontWeight: 600,
    fontSize: 15,
    minWidth: 0,
    overflowWrap: 'anywhere',
    // Patient links are set to nowrap for the desktop table; on mobile we let a
    // long name wrap instead of overflowing.
    '& a': {
      whiteSpace: 'normal !important',
    },
  },
  line: {
    fontSize: 13,
    color: '#595959',
    overflowWrap: 'anywhere',
  },
  lineIcon: {
    marginRight: 6,
    color: '#8c8c8c',
  },
  actionsLine: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: 14,
  },
  // Same grid, indented to align with the primary card past the expand column.
  detailsGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '8px 16px',
    paddingLeft: EXPAND_COLUMN_WIDTH,
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
  },
  fieldLabel: {
    fontSize: 11,
    color: '#8c8c8c',
    textTransform: 'uppercase',
    letterSpacing: 0.3,
  },
  fieldValue: {
    fontSize: 14,
    color: '#1c1c1c',
    overflowWrap: 'anywhere',
  },
  // Lets a field take the full width of the 2-column details grid.
  spanTwo: {
    gridColumn: '1 / -1',
  },
  // The desktop table shows these payloads in a tooltip, which is unusable on
  // touch, so the expanded panel prints them instead.
  payload: {
    margin: 0,
    maxHeight: 200,
    overflow: 'auto',
    whiteSpace: 'pre-wrap',
    overflowWrap: 'anywhere',
    fontSize: 12,
    color: '#1c1c1c',
  },
})

const PATIENT_SHAPE = PropTypes.shape({
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  firstName: PropTypes.string,
  lastName: PropTypes.string,
  practiceMrn: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
})

const FULFILLED_ORDER_SHAPE = PropTypes.shape({
  createdAt: PropTypes.string,
  encounterId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  orderNumber: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  remoteCheckId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  reportId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  requestData: PropTypes.oneOfType([PropTypes.string, PropTypes.shape({})]),
  patient: PATIENT_SHAPE,
  report: PropTypes.shape({
    errorMessages: PropTypes.string,
    eventsSucceeded: PropTypes.string,
  }),
})

// dayjs(undefined) renders today's date, so only format a real value.
function orderDate(date) {
  return date ? dayjs(date).format('MMM D, YYYY') : null
}

function Field({ label, children = null, span = false }) {
  const classes = useStyles()
  return (
    <div
      className={span ? `${classes.field} ${classes.spanTwo}` : classes.field}
    >
      <span className={classes.fieldLabel}>{label}</span>
      <span className={classes.fieldValue}>{children}</span>
    </div>
  )
}

Field.propTypes = {
  label: PropTypes.string.isRequired,
  children: PropTypes.node,
  span: PropTypes.bool,
}

// Same patient link the desktop columns render: the Orders tables link to the
// patient without the edit shortcut other listings show.
function PatientName({ patient = null }) {
  if (!patient?.id) return <span>No Patient</span>

  return (
    <Link to={toPath.patients.patient({ id: `${patient.id}` })}>
      {patient.lastName}, {patient.firstName}
    </Link>
  )
}

PatientName.propTypes = {
  patient: PATIENT_SHAPE,
}

// Line 2 of every card: MRN and the row's main date, empties dropped so there
// is never a dangling separator.
function IdentityLine({ patient = null, date = null }) {
  const classes = useStyles()
  const text = [
    patient?.practiceMrn ? `MRN: ${patient.practiceMrn}` : null,
    orderDate(date),
  ]
    .filter(Boolean)
    .join(' · ')

  if (!text) return null

  return <div className={classes.line}>{text}</div>
}

IdentityLine.propTypes = {
  patient: PATIENT_SHAPE,
  date: PropTypes.string,
}

// Mirrors the desktop Status / Errors columns: same icon colours, with the full
// payloads available in the expanded panel.
function OrderStatusLine({ order }) {
  const classes = useStyles()
  const { errorMessages, eventsSucceeded } = order.report || {}

  if (!eventsSucceeded && !errorMessages) return null

  return (
    <div className={classes.line}>
      {eventsSucceeded && (
        <span>
          <DatabaseOutlined
            className={classes.lineIcon}
            style={{ color: 'green' }}
          />
          Processed
        </span>
      )}
      {Boolean(eventsSucceeded && errorMessages) && ' · '}
      {errorMessages && (
        <span>
          <DatabaseOutlined
            className={classes.lineIcon}
            style={{ color: 'red' }}
          />
          Errors
        </span>
      )}
    </div>
  )
}

OrderStatusLine.propTypes = {
  order: FULFILLED_ORDER_SHAPE.isRequired,
}

// Missing tab: all six desktop columns fit on the card, so these rows need no
// expand toggle.
export function MissingOrderMobileCard({ order }) {
  const classes = useStyles()
  const { cptCode, icd10Code, patient, status, tos } = order
  const codesLine = [
    cptCode ? `CPT ${cptCode}` : null,
    icd10Code ? `ICD ${icd10Code}` : null,
  ]
    .filter(Boolean)
    .join(' · ')

  return (
    <div className={classes.card}>
      <div className={classes.name}>
        <PatientName patient={patient} />
      </div>
      <IdentityLine patient={patient} date={tos} />
      {codesLine && <div className={classes.line}>{codesLine}</div>}
      <div className={classes.line}>Status: {status || '—'}</div>
    </div>
  )
}

MissingOrderMobileCard.propTypes = {
  order: PropTypes.shape({
    cptCode: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    icd10Code: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    status: PropTypes.string,
    tos: PropTypes.string,
    patient: PATIENT_SHAPE,
  }).isRequired,
}

// Inbox tab: nine desktop columns, so the payload-heavy ones (events, errors
// and the raw request) move behind the expand toggle.
export function FulfilledOrderMobileCard({ order }) {
  const classes = useStyles()
  const {
    createdAt,
    encounterId,
    orderNumber,
    patient,
    remoteCheckId,
    reportId,
  } = order
  const idsLine = [
    orderNumber ? `Order ${orderNumber}` : null,
    encounterId ? `Encounter ${encounterId}` : null,
  ]
    .filter(Boolean)
    .join(' · ')

  const hasReport = Boolean(reportId || remoteCheckId)
  const reportUrl = reportId
    ? `/reports/${reportId}`
    : `/remote-checks/${remoteCheckId}`

  const reportButton = (
    <Button
      disabled={!hasReport}
      type="link"
      size="small"
      icon={<FileSearchOutlined />}
      style={{ paddingLeft: 0 }}
    >
      {hasReport ? 'View report' : 'No report'}
    </Button>
  )

  return (
    <div className={classes.card}>
      <div className={classes.name}>
        <PatientName patient={patient} />
      </div>
      <IdentityLine patient={patient} date={createdAt} />
      {idsLine && <div className={classes.line}>{idsLine}</div>}
      <OrderStatusLine order={order} />
      <div className={classes.actionsLine}>
        {hasReport ? <Link to={reportUrl}>{reportButton}</Link> : reportButton}
      </div>
    </div>
  )
}

FulfilledOrderMobileCard.propTypes = {
  order: FULFILLED_ORDER_SHAPE.isRequired,
}

// Fields hidden behind the per-row expand toggle on the Inbox tab.
export function FulfilledOrderMobileDetails({ order }) {
  const classes = useStyles()
  const { requestData } = order
  const { errorMessages, eventsSucceeded } = order.report || {}

  return (
    <div className={classes.detailsGrid}>
      <Field label="Status" span>
        {eventsSucceeded ? (
          <pre className={classes.payload}>{eventsSucceeded}</pre>
        ) : (
          '—'
        )}
      </Field>
      <Field label="Errors" span>
        {errorMessages ? (
          <pre className={classes.payload}>{errorMessages}</pre>
        ) : (
          '—'
        )}
      </Field>
      <Field label="Raw" span>
        {requestData ? (
          <pre className={classes.payload}>
            {JSON.stringify(requestData, null, 2)}
          </pre>
        ) : (
          '—'
        )}
      </Field>
    </div>
  )
}

FulfilledOrderMobileDetails.propTypes = {
  order: FULFILLED_ORDER_SHAPE.isRequired,
}
